<template>
  <div v-if="visible" class="download-dialog-overlay" @click="handleOverlayClick">
    <div class="download-dialog" @click.stop>
      <div class="dialog-header">
        <div class="header-content">
          <h3 class="dialog-title">请选择下载格式</h3>
          <button class="close-btn" @click="handleOverlayClick">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M18 6L6 18M6 6L18 18" stroke="#666" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </button>
        </div>
      </div>
      
      <div class="dialog-content">
        <div class="format-buttons">
          <button 
            class="format-btn" 
            :class="{ active: selectedFormat === 'png' }"
            @click="selectFormat('png')"
            @mouseenter="handleMouseEnter($event)"
            @mouseleave="handleMouseLeave($event)"
          >
            <div class="format-info">
              <span class="format-name">PNG</span>
              <span class="format-desc">透明背景，高质量</span>
            </div>
            <img 
              :src="selectedFormat === 'png' ? require('@/assets/static/icon/组 76.png') : require('@/assets/static/icon/组 75.png')" 
              alt="" 
              class="format-icon"
            />
          </button>

          <button 
            class="format-btn" 
            :class="{ active: selectedFormat === 'jpg' }"
            @click="selectFormat('jpg')"
            @mouseenter="handleMouseEnter($event)"
            @mouseleave="handleMouseLeave($event)"
          >
            <div class="format-info">
              <span class="format-name">JPG</span>
              <span class="format-desc">白色背景，小文件</span>
            </div>
            <img 
              :src="selectedFormat === 'jpg' ? require('@/assets/static/icon/组 76.png') : require('@/assets/static/icon/组 75.png')" 
              alt="" 
              class="format-icon"
            />
          </button>

          <button 
            class="format-btn" 
            :class="{ active: selectedFormat === 'svg' }"
            @click="selectFormat('svg')"
            @mouseenter="handleMouseEnter($event)"
            @mouseleave="handleMouseLeave($event)"
          >
            <div class="format-info">
              <span class="format-name">SVG</span>
              <span class="format-desc">矢量格式，可编辑</span>
            </div>
            <img 
              :src="selectedFormat === 'svg' ? require('@/assets/static/icon/组 76.png') : require('@/assets/static/icon/组 75.png')" 
              alt="" 
              class="format-icon"
            />
          </button>

          <button 
            class="format-btn custom-btn" 
            :class="{ active: selectedFormat === 'custom' }"
            @click="selectFormat('custom')"
            @mouseenter="handleMouseEnter($event)"
            @mouseleave="handleMouseLeave($event)"
          >
            <div class="format-info">
              <span class="format-name">自定义尺寸和格式</span>
              <span class="format-desc">透明背景，高质量</span>
            </div>
            <img 
              :src="selectedFormat === 'custom' ? require('@/assets/static/icon/组 76.png') : require('@/assets/static/icon/组 75.png')" 
              alt="" 
              class="format-icon"
            />
          </button>
        </div>

        <!-- 自定义尺寸输入框 -->
        <div v-if="showCustomInput" class="custom-settings">
          <div class="input-group">
            <label class="input-label">自定义尺寸</label>
            <div class="size-inputs">
              <input 
                v-model="customWidth" 
                type="text" 
                class="size-input"
                placeholder="宽度"
              />
              <span class="size-separator">×</span>
              <input 
                v-model="customHeight" 
                type="text" 
                class="size-input"
                placeholder="高度"
              />
            </div>
          </div>
          <div class="input-group">
            <label class="input-label">自定义格式</label>
            <select v-model="customFormat" class="format-select">
              <option value="png">PNG</option>
              <option value="jpg">JPG</option>
              <option value="svg">SVG</option>
            </select>
          </div>
          <button class="custom-download-btn" @click="confirmCustom" :disabled="!isValidCustomSize">下载</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch } from 'vue'
import { ElMessage } from 'element-plus'

const props = defineProps({
  visible: {
    type: Boolean,
    default: false
  },
  userInfo: {
    type: Object,
    default: () => ({})
  },
  logoData: {
    type: Object,
    default: () => ({})
  }
})

const emit = defineEmits(['close', 'confirm', 'show-vip-modal'])

// 判断是否为VIP用户
const isVip = computed(() => {
  return props.userInfo.member_level && parseInt(props.userInfo.member_level) > 1
})

const selectedFormat = ref('png')
const showCustomInput = ref(false)
const customWidth = ref(1920)
const customHeight = ref(1080)
const customFormat = ref('png')

// 验证自定义尺寸
const isValidCustomSize = computed(() => {
  return customWidth.value >= 100 && customWidth.value <= 4000 && 
         customHeight.value >= 100 && customHeight.value <= 4000
})

// 选择格式
const selectFormat = (format) => {
  if (format === 'custom') {
    showCustomInput.value = true
    selectedFormat.value = format
  } else {
    selectedFormat.value = format
    confirmDownload(format)
  }
}

// 确认自定义尺寸
const confirmCustom = () => {
  if (isValidCustomSize.value) {
    const customFormatData = {
      format: customFormat.value,
      width: parseInt(customWidth.value),
      height: parseInt(customHeight.value),
      isCustom: true
    }
    confirmDownload(customFormatData)
  } else {
    alert('请输入有效的尺寸')
  }
}

// 将SVG转换为图片并下载 - 从AILogoGenerator复制的正确逻辑
const downloadSvgAsImage = (svgContent, filename, format, customWidth = null, customHeight = null) => {
  return new Promise((resolve, reject) => {
    try {
      // 创建一个临时的SVG元素来获取尺寸
      const parser = new DOMParser()
      const svgDoc = parser.parseFromString(svgContent, 'image/svg+xml')
      const svgElement = svgDoc.documentElement
      
      // 获取SVG的原始尺寸
      let width = 800 // 默认宽度
      let height = 600 // 默认高度
      
      // 如果指定了自定义尺寸，使用自定义尺寸
      if (customWidth && customHeight) {
        width = customWidth
        height = customHeight
        
        // 简单直接的方法：修改SVG尺寸和viewBox
        svgElement.setAttribute('width', width)
        svgElement.setAttribute('height', height)

        // 调整背景矩形
        const backgroundRect = svgElement.querySelector('rect[id="bg"]')
        if (backgroundRect) {
          backgroundRect.setAttribute('width', "2000%")
          backgroundRect.setAttribute('height', "2000%")
          backgroundRect.setAttribute('x', '-1000%')
          backgroundRect.setAttribute('y', '-1000%')
        }
        
        // 重新序列化SVG内容
        const serializer = new XMLSerializer()
        svgContent = serializer.serializeToString(svgElement)
      } else {
        // 否则从SVG中获取尺寸
        const viewBox = svgElement.getAttribute('viewBox')
        if (viewBox) {
          const [, , vbWidth, vbHeight] = viewBox.split(' ').map(Number)
          if (vbWidth && vbHeight) {
            width = vbWidth
            height = vbHeight
          }
        } else {
          const svgWidth = svgElement.getAttribute('width')
          const svgHeight = svgElement.getAttribute('height')
          if (svgWidth && svgHeight) {
            width = parseInt(svgWidth)
            height = parseInt(svgHeight)
          }
        }
      }
      
      // 设置高分辨率（自定义尺寸时使用1倍分辨率，其他情况使用2倍）
      const scale = (customWidth && customHeight) ? 1 : 2
      const canvasWidth = width * scale
      const canvasHeight = height * scale
      
      // 创建canvas
      const canvas = document.createElement('canvas')
      canvas.width = canvasWidth
      canvas.height = canvasHeight
      const ctx = canvas.getContext('2d')
      
      // 设置高质量渲染
      ctx.imageSmoothingEnabled = true
      ctx.imageSmoothingQuality = 'high'
      
      // 如果是JPG格式，先填充白色背景
      if (format === 'jpg') {
        ctx.fillStyle = '#ffffff'
        ctx.fillRect(0, 0, canvasWidth, canvasHeight)
      }
      
      // 创建图片对象
      const img = new Image()
      img.onload = function() {
        // 绘制图片到canvas
        ctx.drawImage(img, 0, 0, canvasWidth, canvasHeight)
        
        // 转换为blob并下载
        canvas.toBlob((blob) => {
          if (blob) {
            const url = URL.createObjectURL(blob)
            const link = document.createElement('a')
            link.href = url
            link.download = `${filename}.${format}`
            document.body.appendChild(link)
            link.click()
            document.body.removeChild(link)
            URL.revokeObjectURL(url)
            
            ElMessage.success(`${format.toUpperCase()}下载成功`)
            resolve()
          } else {
            reject(new Error('转换失败'))
          }
        }, format === 'jpg' ? 'image/jpeg' : 'image/png', 0.95)
      }
      
      img.onerror = function() {
        reject(new Error('图片加载失败'))
      }
      
      // 将修改后的SVG转换为Data URL
      const svgBlob = new Blob([svgContent], { type: 'image/svg+xml;charset=utf-8' })
      const svgUrl = URL.createObjectURL(svgBlob)
      img.src = svgUrl
      
    } catch (error) {
      reject(error)
    }
  })
}

// 下载SVG格式
const downloadSvgFormat = (svgContent, filename) => {
  const svgBlob = new Blob([svgContent], { type: 'image/svg+xml' })
  const url = URL.createObjectURL(svgBlob)
  
  const link = document.createElement('a')
  link.href = url
  link.download = `${filename}.svg`
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  
  URL.revokeObjectURL(url)
  ElMessage.success('SVG下载成功')
}

// 确认下载
const confirmDownload = async (format) => {
  if (!props.logoData) {
    ElMessage.error('没有可下载的LOGO数据')
    return
  }

  // 获取SVG内容，支持多种数据结构
  let svgContent = null
  let filename = 'logo'
  
  // 处理历史记录页面的数据结构 (logoData.logoData.svg)
  if (props.logoData.logoData && props.logoData.logoData.svg) {
    svgContent = props.logoData.logoData.svg
    filename = (props.logoData.brandName || 'logo').replace(/[^a-zA-Z0-9\u4e00-\u9fa5]/g, '_')
  }
  // 处理AILogoGenerator页面的数据结构 (templateSvg 或 svgData)
  else if (props.logoData.templateSvg || props.logoData.svgData) {
    svgContent = props.logoData.templateSvg || props.logoData.svgData
    filename = (props.logoData.title || 'logo').replace(/[^a-zA-Z0-9\u4e00-\u9fa5]/g, '_')
  }
  
  if (!svgContent) {
    ElMessage.error('没有可下载的SVG数据')
    return
  }
  
  try {
    if (format === 'svg') {
      // 下载SVG格式
      downloadSvgFormat(svgContent, filename)
    } else if (format.isCustom) {
      // 自定义尺寸下载
      await downloadSvgAsImage(svgContent, filename, format.format, format.width, format.height)
    } else {
      // 转换为图片格式下载
      await downloadSvgAsImage(svgContent, filename, format)
    }
  } catch (error) {
    console.error('下载失败:', error)
    ElMessage.error('下载失败，请稍后重试')
  }
  
  emit('close')
}

// 点击遮罩层关闭
const handleOverlayClick = () => {
  emit('close')
}

// 处理鼠标移入事件
const handleMouseEnter = (event) => {
  const icon = event.currentTarget.querySelector('.format-icon')
  if (icon) {
    icon.src = require('@/assets/static/icon/组 76.png')
  }
}

// 处理鼠标移出事件
const handleMouseLeave = (event) => {
  const icon = event.currentTarget.querySelector('.format-icon')
  if (icon) {
    icon.src = require('@/assets/static/icon/组 75.png')
  }
}

// 监听visible变化，重置状态
watch(() => props.visible, (newVal) => {
  if (newVal) {
    selectedFormat.value = 'png'
    showCustomInput.value = false
    customWidth.value = 1920
    customHeight.value = 1080
    customFormat.value = 'png'
  }
})
</script>

<style scoped>
.download-dialog-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
}

.download-dialog {
  background: white;
  padding: 30px;
  border-radius: 12px;
  min-width: 300px;
  text-align: center;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.15);
  position: relative;
  width: 400px;
}

.dialog-header {
  margin-bottom: 20px;
}

.header-content {
  display: flex;
  align-items: center;
  position: relative;
  justify-content: center;
}

.dialog-title {
  margin: 0;
  color: #333;
  font-size: 18px;
}

.close-btn {
  width: 30px;
  height: 30px;
  border-radius: 16px;
  border: 0;
  background: #E2E2E2;
  display: flex;
  align-items: center;
  justify-content: center;
  position: absolute;
  right: 0;
  cursor: pointer;
  transition: background 0.3s ease;
}

.close-btn:hover {
  background: #f5f5f5;
}

.dialog-content {
  text-align: left;
}

.format-buttons {
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-bottom: 20px;
}

.format-btn {
  padding: 16px 26px;
  border: 0;
  border-radius: 6px;
  background: #F2F7F7;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  font-size: 14px;
  text-align: left;
  width: 100%;
}

.format-btn:hover {
  background: #F2F7F7;
}

.format-btn.active {
  background: #F2F7F7;
}

.format-info {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
}

.format-name {
  font-weight: bold;
  color: #333;
}

.format-desc {
  color: #666;
  font-size: 12px;
  margin-top: 4px;
}

.format-icon {
  width: 35px;
  height: 35px;
  align-self: center;
}

.custom-settings {
  border-radius: 8px;
  margin-bottom: 20px;
  box-sizing: border-box;
}

.input-group {
  margin-bottom: 16px;
}

.input-label {
  display: block;
  margin-bottom: 8px;
  font-weight: 500;
  color: #333;
}

.size-inputs {
  display: flex;
  align-items: center;
  background: #F2F7F7;
  justify-content: space-between;
  gap: 8px;
}

.size-input {
  width: 150px;
  padding: 8px 12px;
  border-radius: 5px;
  text-align: center;
  font-weight: bold;
  font-size: 14px;
  background: #F2F7F7;
  border: none;
}

.size-separator {
  color: #666;
}

.format-select {
  width: 100%;
  background: #F2F7F7 url('@/assets/static/icon/容器.png') no-repeat right 12px center;
  padding: 18px 12px;
  border: 0;
  border-radius: 5px;
  font-weight: bold;
  font-size: 14px;
  text-align: center;
  cursor: pointer;
  appearance: none;
  -webkit-appearance: none;
  -moz-appearance: none;
}

.custom-download-btn {
  width: 100%;
  padding: 12px;
  background: #4285f4;
  color: white;
  border: 0;
  border-radius: 33px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  transition: background 0.3s ease;
}

.custom-download-btn:hover:not(:disabled) {
  background: #3367d6;
}

.custom-download-btn:disabled {
  background: #ccc;
  cursor: not-allowed;
}

/* 响应式设计 */
@media (max-width: 480px) {
  .download-dialog {
    width: 95vw;
    margin: 20px;
    padding: 20px;
  }
  
  .format-btn {
    padding: 12px 16px;
  }
  
  .format-name {
    font-size: 14px;
  }
  
  .format-desc {
    font-size: 11px;
  }
}
</style>